import { createApi } from '@reduxjs/toolkit/query/react';
import { User } from '../types';
import { API_BASE_URL } from '../config';
import { createBaseQueryWithAuth } from './baseQueryWithAuth';

export interface Submission {
  _id: string;
  topic: string;
  user: User;
  status: string;
}

export interface CreateAdminRequest {
  name: string;
  email: string;
  password: string;
  designation: string;
}

export interface Invite {
  _id: string;
  email: string;
  name?: string;
  designation?: string;
  role: 'admin' | 'user';
  status: 'pending' | 'accepted' | 'revoked' | 'expired';
  expiresAt: string;
  acceptedAt?: string;
  invitedBy?: { _id: string; name: string; email: string };
  createdAt: string;
}

export interface CreateInviteRequest {
  email: string;
  name?: string;
  designation?: string;
  role?: 'admin' | 'user';
}

export const adminApi = createApi({
  reducerPath: 'adminApi',
  baseQuery: createBaseQueryWithAuth(`${API_BASE_URL}/admin`),
  tagTypes: ['Users', 'Admins', 'Invites'],
  endpoints: (builder) => ({
    getUsers: builder.query<User[], void>({
      query: () => 'users',
      providesTags: ['Users'],
    }),
    getSubmissions: builder.query<Submission[], void>({
      query: () => 'submissions',
    }),
    // Superadmin-only
    getAdmins: builder.query<User[], void>({
      query: () => 'admins',
      providesTags: ['Admins'],
    }),
    createAdmin: builder.mutation<User, CreateAdminRequest>({
      query: (body) => ({
        url: 'admins',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Admins', 'Users'],
    }),
    updateUserRole: builder.mutation<User, { userId: string; role: 'superadmin' | 'admin' | 'user' }>({
      query: ({ userId, role }) => ({
        url: `users/${userId}/role`,
        method: 'PATCH',
        body: { role },
      }),
      invalidatesTags: ['Admins', 'Users'],
    }),
    deleteUser: builder.mutation<{ message: string }, string>({
      query: (userId) => ({
        url: `users/${userId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Admins', 'Users'],
    }),
    // Invites
    getInvites: builder.query<Invite[], void>({
      query: () => 'invites',
      providesTags: ['Invites'],
    }),
    createInvite: builder.mutation<Invite, CreateInviteRequest>({
      query: (body) => ({
        url: 'invites',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Invites'],
    }),
    revokeInvite: builder.mutation<{ message: string }, string>({
      query: (id) => ({
        url: `invites/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Invites'],
    }),
    resendInvite: builder.mutation<{ message: string; acceptUrl: string }, string>({
      query: (id) => ({
        url: `invites/${id}/resend`,
        method: 'POST',
      }),
      invalidatesTags: ['Invites'],
    }),
  }),
});

export const {
  useGetUsersQuery,
  useGetSubmissionsQuery,
  useGetAdminsQuery,
  useCreateAdminMutation,
  useUpdateUserRoleMutation,
  useDeleteUserMutation,
  useGetInvitesQuery,
  useCreateInviteMutation,
  useRevokeInviteMutation,
  useResendInviteMutation,
} = adminApi;
